import {Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {Menu, Transition} from '@headlessui/react';
import ChevronDownIcon from "@heroicons/react/solid/ChevronDownIcon";
import PlusSmIcon from "@heroicons/react/solid/PlusSmIcon";

DividerAddButton.propTypes = {
    label: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string,
    showLabel: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    secondaryActionLabel: PropTypes.string,
    secondaryActions: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        ariaLabel: PropTypes.string,
        disabled: PropTypes.bool,
        onClick: PropTypes.func.isRequired,
    })),
};

DividerAddButton.defaultProps = {
    showLabel: false,
    disabled: false,
    secondaryActionLabel: 'More Actions',
    secondaryActions: [],
};

export default function DividerAddButton({className, label, ariaLabel, showLabel, disabled, secondaryActionLabel, secondaryActions, ...props}) {
    const computedAriaLabel = ariaLabel || label;
    const hasSecondaryActions = showLabel && secondaryActions.length > 0;

    if (hasSecondaryActions) {
        const {onClick, ...containerProps} = props;

        return (
            <div
                className={classNames(
                    "w-full relative group",
                    disabled ? "cursor-not-allowed opacity-50 pointer-events-none" : "cursor-pointer",
                    className
                )}
                {...containerProps}
            >
                <div className="flex items-center">
                    <div className="grow border-t-2 border-primary-600 group-hover:border-primary-700 mr-1 rounded-full" />
                    <div className="relative inline-flex h-6 overflow-visible rounded-full bg-primary-600 text-white group-hover:bg-primary-700">
                        <button
                            type="button"
                            aria-label={computedAriaLabel}
                            disabled={disabled}
                            onClick={onClick}
                            className="relative inline-flex h-6 items-center rounded-l-full pl-1.5 pr-2 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:bg-primary-700"
                        >
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full">
                                <PlusSmIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                            <span className="text-xs font-semibold leading-none whitespace-nowrap">{label}</span>
                        </button>
                        <Menu as="div" className="relative inline-flex">
                            <Menu.Button
                                type="button"
                                aria-label={secondaryActionLabel}
                                disabled={disabled}
                                className="inline-flex h-6 w-8 items-center justify-center rounded-r-full border-l border-white/60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:bg-primary-700"
                                onClick={event => event.stopPropagation()}>
                                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                            </Menu.Button>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95">
                                <Menu.Items className="menu-container absolute left-1/2 z-30 mt-7 w-40 -translate-x-1/2 origin-top">
                                    <div className="flex flex-col">
                                        {secondaryActions.map(action => (
                                            <Menu.Item key={action.label} disabled={action.disabled}>
                                                {({active, close}) => (
                                                    <button
                                                        type="button"
                                                        aria-label={action.ariaLabel || action.label}
                                                        disabled={action.disabled}
                                                        className={classNames(
                                                            "block w-[calc(100%-0.75rem)] text-left rounded my-1 mx-1.5 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-panels-100",
                                                            active && "bg-panels-100",
                                                            action.disabled && "cursor-not-allowed opacity-50"
                                                        )}
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            action.onClick(event);
                                                            close();
                                                        }}>
                                                        {action.label}
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        ))}
                                    </div>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    </div>
                    <div className="grow border-t-2 border-primary-600 group-hover:border-primary-700 ml-1 rounded-full" />
                </div>
            </div>
        );
    }

    return (
        <div
            className={classNames(
                "w-full h-6 relative group",
                disabled ? "cursor-not-allowed opacity-50 pointer-events-none" : "cursor-pointer",
                className
            )}
            {...props}
        >
            {showLabel ? (
                <div className="flex items-center">
                    <div className="grow border-t-2 border-primary-600 group-hover:border-primary-700 mr-1 rounded-full" />
                    <button
                        type="button"
                        aria-label={computedAriaLabel}
                        disabled={disabled}
                        className="relative inline-flex items-center pl-1.5 pr-3 h-6 font-medium rounded-full text-white bg-primary-600 group-hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:bg-primary-700"
                    >
                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full">
                            <PlusSmIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <span className="text-xs font-semibold leading-none whitespace-nowrap">{label}</span>
                    </button>
                    <div className="grow border-t-2 border-primary-600 group-hover:border-primary-700 ml-1 rounded-full" />
                </div>
            ) : (
                <>
                    <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between" aria-hidden="true">
                        <div className="grow border-t-2 border-primary-600 mr-2.5 rounded-full group-hover:bg-primary-700" />
                        <div className="grow border-t-2 border-primary-600 ml-2.5 rounded-full group-hover:bg-primary-700" />
                    </div>
                    <div className="relative flex h-full items-center justify-center">
                        <button
                            type="button"
                            aria-label={computedAriaLabel}
                            disabled={disabled}
                            className="relative inline-flex items-center h-5 w-5 font-medium rounded-full text-white bg-primary-600 group-hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:bg-primary-700"
                        >
                            <PlusSmIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};
